import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../../component/layout";
import { toastify } from "../../../../utils/toast";
import { makeRequest2 } from "../../../../utils/makeRequest";
import {
    getAllDeposits,
    refundDeposit,
    updateDeposit,
    payDeposit,
} from "../../../../utils/urls";
import { getItem } from "../../../../utils/localStorage";

// ─── tiny helpers ────────────────────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString("en-KE", { minimumFractionDigits: 2 });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const toInputDate = (d) => (d ? new Date(d).toISOString().split("T")[0] : "");

const MONTH_OPTIONS = [
    { label: "All Months", value: null },
    { label: "January", value: 1 },
    { label: "February", value: 2 },
    { label: "March", value: 3 },
    { label: "April", value: 4 },
    { label: "May", value: 5 },
    { label: "June", value: 6 },
    { label: "July", value: 7 },
    { label: "August", value: 8 },
    { label: "September", value: 9 },
    { label: "October", value: 10 },
    { label: "November", value: 11 },
    { label: "December", value: 12 },
];

const buildYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [{ label: "All Years", value: null }];
    for (let y = currentYear + 1; y >= currentYear - 5; y--) {
        years.push({ label: String(y), value: y });
    }
    return years;
};

// ─── Receipt visual styles (mirrors PaymentReceipt.jsx) ─────────────────────
const receiptStyles = {
    receiptContainer: {
        width: "100%",
        maxWidth: "600px",
        margin: "0 auto",
        backgroundColor: "white",
        borderRadius: "0.5rem",
        overflow: "hidden",
        border: "1px solid #e5e7eb",
    },
    header: {
        backgroundColor: "#2196f3",
        padding: "1.5rem",
        color: "white",
        textAlign: "center",
        width: "100%",
    },
    headerTitle: {
        fontSize: "1.25rem",
        fontWeight: "600",
        marginBottom: "0.25rem",
        letterSpacing: "0.05em",
    },
    headerSubtitle: {
        fontSize: "0.95rem",
        opacity: "0.9",
        lineHeight: "1.5",
    },
    content: {
        padding: "1.5rem",
    },
    detailsGrid: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: "1px solid #e5e7eb",
        padding: "0.5rem 0",
        marginBottom: "0.5rem",
        lineHeight: "1.5",
    },
    label: {
        color: "#4b5563",
        fontSize: "0.875rem",
        fontWeight: "500",
        flex: "1",
    },
    value: {
        color: "#1f2937",
        fontSize: "0.875rem",
        textAlign: "right",
        flex: "1",
    },
    section: {
        marginBottom: "1.5rem",
    },
    sectionTitle: {
        color: "#1f2937",
        fontSize: "1rem",
        fontWeight: "600",
        marginBottom: "1rem",
        textAlign: "center",
    },
    statusLabel: {
        fontWeight: "600",
        fontSize: "0.875rem",
        flex: "1",
    },
    statusValue: {
        fontWeight: "600",
        textAlign: "right",
        fontSize: "0.875rem",
        flex: "1",
    },
    footer: {
        paddingTop: "1.5rem",
        marginTop: "1rem",
        textAlign: "center",
        color: "#6b7280",
        fontSize: "0.8rem",
        lineHeight: "1.7",
        borderTop: "1px solid #e5e7eb",
    },
};

const FacilityLevyDeposits = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    const receiptRef = useRef(null);

    const [deposits, setDeposits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState("");
    const [activeTabIndex, setActiveTabIndex] = useState(0);

    const [facilityId, setFacilityId] = useState(null);

    // Pagination (server-side)
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // Period filter (month/year)
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);
    const yearOptions = buildYearOptions();

    // Facility-wide stats (independent of the current page, respects period filter)
    const [stats, setStats] = useState({
        totalDepositAmount: 0,
        totalHeld: 0,
        totalRefunded: 0,
        totalDue: 0,
        totalCount: 0,
    });

    // Export state
    const [isExportingExcel, setIsExportingExcel] = useState(false);
    const [isExportingPDF, setIsExportingPDF] = useState(false);

    // Refund modal
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [selectedDeposit, setSelectedDeposit] = useState(null);
    const [refundAmount, setRefundAmount] = useState("");
    const [refundDate, setRefundDate] = useState("");
    const [refundReference, setRefundReference] = useState("");
    const [isRefunding, setIsRefunding] = useState(false);

    // Edit modal
    const [showEditModal, setShowEditModal] = useState(false);
    const [editDeposit, setEditDeposit] = useState(null);
    const [editDatePaid, setEditDatePaid] = useState("");
    const [editPaymentReference, setEditPaymentReference] = useState("");
    const [editReceiptNumber, setEditReceiptNumber] = useState("");
    const [isUpdating, setIsUpdating] = useState(false);

    // Record Payment modal (NEW)
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentDeposit, setPaymentDeposit] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState("");
    const [paymentDate, setPaymentDate] = useState("");
    const [paymentReference, setPaymentReference] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("");
    const [isPaying, setIsPaying] = useState(false);

    // Receipt view modal
    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [receiptDeposit, setReceiptDeposit] = useState(null);

    useEffect(() => {
        const init = async () => {
            const fid = await getItem("selectedFacilityId");
            setFacilityId(fid);
        };
        init();
    }, []);

    const buildQueryParams = (overrides = {}) => {
        const params = {};
        if (selectedMonth) params.month = String(selectedMonth);
        if (selectedYear) params.year = String(selectedYear);
        return { ...params, ...overrides };
    };

    const fetchDeposits = async () => {
        const fid = facilityId || (await getItem("selectedFacilityId"));
        if (!fid) return;
        setLoading(true);
        try {
            const qs = new URLSearchParams(
                buildQueryParams({
                    page: String(page),
                    limit: String(rowsPerPage),
                })
            ).toString();
            const response = await makeRequest2(`${getAllDeposits}/${fid}?${qs}`, "GET");
            if (response.success) {
                const data = response.data?.data || response.data || [];
                setDeposits(data);
                setTotalRecords(response.data?.pagination?.total ?? data.length);
                if (response.data?.stats) {
                    setStats(response.data.stats);
                }
            } else {
                toast.current?.show({ severity: "error", summary: "Error", detail: response.error || "Failed to fetch deposits" });
            }
        } catch (error) {
            toast.current?.show({ severity: "error", summary: "Error", detail: error.message });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (facilityId) fetchDeposits();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facilityId, page, rowsPerPage, selectedMonth, selectedYear]);

    // Reset to page 1 whenever the period filter changes
    const handleMonthChange = (value) => {
        setSelectedMonth(value);
        setPage(1);
    };
    const handleYearChange = (value) => {
        setSelectedYear(value);
        setPage(1);
    };

    // ── Refund modal handlers ─────────────────────────────────────────────────
    const openRefundModal = (deposit) => {
        setSelectedDeposit(deposit);
        const remaining = (deposit.amount || 0) - (deposit.refundedAmount || 0);
        setRefundAmount(remaining > 0 ? String(remaining) : "");
        setRefundDate(toInputDate(new Date()));
        setRefundReference("");
        setShowRefundModal(true);
    };

    const closeRefundModal = () => {
        setShowRefundModal(false);
        setSelectedDeposit(null);
        setRefundAmount("");
        setRefundDate("");
        setRefundReference("");
    };

    const handleRefund = async () => {
        const fid = facilityId || (await getItem("selectedFacilityId"));
        if (!refundAmount || isNaN(refundAmount) || parseFloat(refundAmount) <= 0) {
            toast.current?.show({ severity: "warn", summary: "Validation Error", detail: "Please enter a valid refund amount", life: 3000 });
            return;
        }
        setIsRefunding(true);
        try {
            const payload = {
                refundAmount: parseFloat(refundAmount),
                refundDate: refundDate || undefined,
                refundReference: refundReference || undefined,
            };
            const response = await makeRequest2(`${refundDeposit}/${fid}/${selectedDeposit._id}`, "POST", payload);
            if (response.success) {
                toastify(response.message || "Deposit refunded successfully", "success");
                closeRefundModal();
                fetchDeposits();
            } else {
                toast.current?.show({ severity: "error", summary: "Error", detail: response.error || "Failed to refund deposit", life: 4000 });
            }
        } catch (error) {
            toast.current?.show({ severity: "error", summary: "Error", detail: error.message, life: 4000 });
        } finally {
            setIsRefunding(false);
        }
    };

    // ── Edit modal handlers ───────────────────────────────────────────────────
    const openEditModal = (deposit) => {
        setEditDeposit(deposit);
        setEditDatePaid(toInputDate(deposit.datePaid));
        setEditPaymentReference(deposit.paymentReference || "");
        setEditReceiptNumber(deposit.receiptNumber || "");
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setEditDeposit(null);
        setEditDatePaid("");
        setEditPaymentReference("");
        setEditReceiptNumber("");
    };

    const handleUpdate = async () => {
        const fid = facilityId || (await getItem("selectedFacilityId"));
        setIsUpdating(true);
        try {
            const payload = {
                datePaid: editDatePaid || undefined,
                paymentReference: editPaymentReference,
                receiptNumber: editReceiptNumber,
            };
            const response = await makeRequest2(`${updateDeposit}/${fid}/${editDeposit._id}`, "PATCH", payload);
            if (response.success) {
                toastify(response.message || "Deposit updated successfully", "success");
                closeEditModal();
                fetchDeposits();
            } else {
                toast.current?.show({ severity: "error", summary: "Error", detail: response.error || "Failed to update deposit", life: 4000 });
            }
        } catch (error) {
            toast.current?.show({ severity: "error", summary: "Error", detail: error.message, life: 4000 });
        } finally {
            setIsUpdating(false);
        }
    };

    // ── Record Payment modal handlers (NEW) ─────────────────────────────────
    const openPaymentModal = (deposit) => {
        setPaymentDeposit(deposit);
        const due = (deposit.amount || 0) - (deposit.amountPaid || 0);
        setPaymentAmount(due > 0 ? String(due) : "");
        setPaymentDate(toInputDate(new Date()));
        setPaymentReference("");
        setPaymentMethod("");
        setShowPaymentModal(true);
    };

    const closePaymentModal = () => {
        setShowPaymentModal(false);
        setPaymentDeposit(null);
        setPaymentAmount("");
        setPaymentDate("");
        setPaymentReference("");
        setPaymentMethod("");
    };

    const handleRecordPayment = async () => {
        const fid = facilityId || (await getItem("selectedFacilityId"));
        if (!paymentAmount || isNaN(paymentAmount) || parseFloat(paymentAmount) <= 0) {
            toast.current?.show({ severity: "warn", summary: "Validation Error", detail: "Please enter a valid payment amount", life: 3000 });
            return;
        }
        setIsPaying(true);
        try {
            const payload = {
                amount: parseFloat(paymentAmount),
                datePaid: paymentDate || undefined,
                paymentReference: paymentReference || undefined,
                paymentMethod: paymentMethod || undefined,
            };
            const response = await makeRequest2(`${payDeposit}/${fid}/${paymentDeposit._id}`, "POST", payload);
            if (response.success) {
                toastify(response.message || "Payment recorded successfully", "success");
                closePaymentModal();
                fetchDeposits();
            } else {
                toast.current?.show({ severity: "error", summary: "Error", detail: response.error || "Failed to record payment", life: 4000 });
            }
        } catch (error) {
            toast.current?.show({ severity: "error", summary: "Error", detail: error.message, life: 4000 });
        } finally {
            setIsPaying(false);
        }
    };

    // ── Receipt modal handlers ────────────────────────────────────────────────
    const openReceiptModal = (deposit) => {
        setReceiptDeposit(deposit);
        setShowReceiptModal(true);
    };

    const closeReceiptModal = () => {
        setShowReceiptModal(false);
        setReceiptDeposit(null);
    };

    const handleDownloadReceiptPDF = async () => {
        try {
            const html2canvas = (await import("html2canvas")).default;
            const { default: jsPDF } = await import("jspdf");

            const element = receiptRef.current;
            if (!element) return;

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: "#ffffff",
            });

            const imgData = canvas.toDataURL("image/jpeg", 1.0);
            const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

            const pageWidth = 210;
            const contentWidth = 170;
            const contentHeight = (canvas.height * contentWidth) / canvas.width;
            const x = (pageWidth - contentWidth) / 2;
            const y = 15;

            pdf.addImage(imgData, "JPEG", x, y, contentWidth, contentHeight, "", "FAST");
            pdf.save(`Deposit_Receipt_${receiptDeposit?.receiptNumber || "deposit"}.pdf`);
        } catch (error) {
            toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to generate PDF", life: 4000 });
        }
    };

    // ── Templates ─────────────────────────────────────────────────────────────
    const statusTemplate = (rowData) => {
        const map = {
            Pending: { color: "#9e9e9e", bg: "#f5f5f5" },
            "Partially Paid": { color: "#8e24aa", bg: "#f3e5f5" },
            Held: { color: "#ff9800", bg: "#fff3e0" },
            Refunded: { color: "#4caf50", bg: "#e8f5e9" },
            "Partially Refunded": { color: "#2196f3", bg: "#e3f2fd" },
            Forfeited: { color: "#f44336", bg: "#ffebee" },
        };
        const s = map[rowData.status] || { color: "#9e9e9e", bg: "#f5f5f5" };
        return (
            <span style={{ fontWeight: "bold", color: s.color, padding: "5px 10px", borderRadius: "15px", backgroundColor: s.bg }}>
                {rowData.status}
            </span>
        );
    };

    const customerTemplate = (rowData) => {
        const c = rowData.customerId;
        if (!c) return "—";
        return c.name || `${c.firstName || ""} ${c.lastName || ""}`.trim() || "—";
    };

    const unitTemplate = (rowData) => rowData.unitId?.name || rowData.unitId?.unitNumber || "—";

    const contractTemplate = (rowData) => rowData.contractId?.contractName || "—";

    // Unpaid deposits show what's still due toward `amount`; anything
    // that's already collected (Held / Refunded / Partially Refunded /
    // Forfeited) shows the refund-relevant balance instead.
    const balanceTemplate = (rowData) => {
        const isUnpaid = rowData.status === "Pending" || rowData.status === "Partially Paid";
        if (isUnpaid) {
            const due = (rowData.amount || 0) - (rowData.amountPaid || 0);
            return `Due: KES ${fmt(due)}`;
        }
        const remaining = (rowData.amount || 0) - (rowData.refundedAmount || 0);
        return `KES ${fmt(remaining)}`;
    };
    const actionTemplate = (rowData) => {
        const isUnpaid = rowData.status === "Pending" || rowData.status === "Partially Paid";
        const canRefund = rowData.status === "Held" || rowData.status === "Partially Refunded";
        return (
            <div className="d-flex flex-nowrap align-items-center gap-2">
                <Button
                    icon="ti ti-eye"
                    className="p-button-rounded p-button-secondary"
                    tooltip="View Receipt"
                    tooltipOptions={{ position: "top" }}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (rowData.receiptNumber) {
                            // Pass who/what was clicked so a duplicated receipt number can't open someone else's receipt
                            const cid = rowData.customerId?._id || rowData.customerId;
                            const qs = new URLSearchParams({ depositId: rowData._id });
                            if (cid) qs.set("customerId", cid);
                            navigate(`/facility/levy_management/deposit-receipt/${rowData.receiptNumber}?${qs.toString()}`);
                        } else {
                            toast.current?.show({
                                severity: "warn",
                                summary: "No Receipt",
                                detail: "This deposit does not have a receipt number"
                            });
                        }
                    }}
                />
                <Button
                    icon="ti ti-edit"
                    className="p-button-rounded p-button-info"
                    tooltip="Edit Deposit"
                    tooltipOptions={{ position: "top" }}
                    onClick={(e) => { e.stopPropagation(); openEditModal(rowData); }}
                />
                <Button
                    icon="ti ti-credit-card"
                    className="p-button-rounded p-button-primary"
                    tooltip={isUnpaid ? "Record Payment" : "Payment already recorded"}
                    tooltipOptions={{ position: "top" }}
                    disabled={!isUnpaid}
                    onClick={(e) => { e.stopPropagation(); openPaymentModal(rowData); }}
                />
                <Button
                    icon="ti ti-receipt-refund"
                    className="p-button-rounded p-button-success"
                    tooltip={canRefund ? "Refund Deposit" : "Nothing held to refund"}
                    tooltipOptions={{ position: "top" }}
                    disabled={!canRefund}
                    onClick={(e) => { e.stopPropagation(); openRefundModal(rowData); }}
                />
            </div>
        );
    };
    const filterDeposits = (list) =>
        list.filter((deposit) => {
            const customerName = customerTemplate(deposit);
            const unitName = unitTemplate(deposit);
            const term = searchKeyword.toLowerCase();
            return (
                deposit.receiptNumber?.toLowerCase().includes(term) ||
                customerName?.toLowerCase().includes(term) ||
                unitName?.toLowerCase().includes(term) ||
                deposit.status?.toLowerCase().includes(term)
            );
        });

    const splitByStatus = (list) => ({
        all: list,
        pending: list.filter((d) => d.status === "Pending" || d.status === "Partially Paid"),
        held: list.filter((d) => d.status === "Held"),
        refunded: list.filter((d) => d.status === "Refunded" || d.status === "Partially Refunded"),
        forfeited: list.filter((d) => d.status === "Forfeited"),
    });

    const allDeposits = filterDeposits(deposits);
    const {
        pending: pendingDeposits,
        held: heldDeposits,
        refunded: refundedDeposits,
        forfeited: forfeitedDeposits,
    } = splitByStatus(allDeposits);
    const getDepositsByTab = (idx) =>
        [allDeposits, pendingDeposits, heldDeposits, refundedDeposits, forfeitedDeposits][idx] || allDeposits;

    const totalHeld = stats.totalHeld;
    const totalRefunded = stats.totalRefunded;
    const totalDepositAmount = stats.totalDepositAmount;
    const totalDue = stats.totalDue;
    const totalDepositsCount = stats.totalCount;

    const fetchFullFilteredDataset = async () => {
        const fid = facilityId || (await getItem("selectedFacilityId"));
        if (!fid) return [];
        const qs = new URLSearchParams(buildQueryParams({ all: "true" })).toString();
        const response = await makeRequest2(`${getAllDeposits}/${fid}?${qs}`, "GET");
        if (!response.success) {
            throw new Error(response.error || "Failed to fetch deposits for export");
        }
        const fullData = response.data?.data || [];
        const searched = filterDeposits(fullData);
        const byTab = splitByStatus(searched);
        return [searched, byTab.pending, byTab.held, byTab.refunded, byTab.forfeited][activeTabIndex] || searched;
    };

    const toExportRows = (list) =>
        list.map((d) => ({
            "Receipt #": d.receiptNumber || "",
            "Customer": customerTemplate(d),
            "Unit": unitTemplate(d),
            "Amount (KES)": d.amount || 0,
            "Paid (KES)": d.amountPaid || 0,
            "Refunded (KES)": d.refundedAmount || 0,
            "Balance (KES)": (d.amount || 0) - (d.refundedAmount || 0),
            "Due (KES)": Math.max((d.amount || 0) - (d.amountPaid || 0), 0),
            "Date Paid": fmtDate(d.datePaid),
            "Status": d.status || "",
        }));

    const buildExportFilenameSuffix = () => {
        const monthLabel = selectedMonth
            ? MONTH_OPTIONS.find((m) => m.value === selectedMonth)?.label
            : "AllMonths";
        const yearLabel = selectedYear ? String(selectedYear) : "AllYears";
        return `${monthLabel}_${yearLabel}`;
    };

    const handleExportExcel = async () => {
        setIsExportingExcel(true);
        try {
            const rowsData = await fetchFullFilteredDataset();
            if (!rowsData.length) {
                toast.current?.show({ severity: "warn", summary: "Nothing to export", detail: "No deposits match the current filters" });
                return;
            }
            const XLSX = await import("xlsx");
            const worksheet = XLSX.utils.json_to_sheet(toExportRows(rowsData));
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Levy Deposits");
            XLSX.writeFile(workbook, `Levy_Deposits_${buildExportFilenameSuffix()}.xlsx`);
        } catch (error) {
            toast.current?.show({ severity: "error", summary: "Export Failed", detail: error.message, life: 4000 });
        } finally {
            setIsExportingExcel(false);
        }
    };

    const handleExportPDF = async () => {
        setIsExportingPDF(true);
        try {
            const rowsData = await fetchFullFilteredDataset();
            if (!rowsData.length) {
                toast.current?.show({ severity: "warn", summary: "Nothing to export", detail: "No deposits match the current filters" });
                return;
            }
            const { default: jsPDF } = await import("jspdf");
            await import("jspdf-autotable");

            const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
            const monthLabel = selectedMonth
                ? MONTH_OPTIONS.find((m) => m.value === selectedMonth)?.label
                : "All Months";
            const yearLabel = selectedYear ? String(selectedYear) : "All Years";

            doc.setFontSize(14);
            doc.text("Levy Deposits Report", 14, 15);
            doc.setFontSize(10);
            doc.text(`Period: ${monthLabel} ${yearLabel}`, 14, 21);

            const body = rowsData.map((d) => [
                d.receiptNumber || "—",
                customerTemplate(d),
                unitTemplate(d),
                `KES ${fmt(d.amount)}`,
                `KES ${fmt(d.refundedAmount)}`,
                balanceTemplate(d),
                fmtDate(d.datePaid),
                d.status || "",
            ]);

            doc.autoTable({
                startY: 26,
                head: [["Receipt #", "Customer", "Unit", "Amount", "Refunded", "Balance", "Date Paid", "Status"]],
                body,
                styles: { fontSize: 8 },
                headStyles: { fillColor: [33, 150, 243] },
            });

            doc.save(`Levy_Deposits_${buildExportFilenameSuffix()}.pdf`);
        } catch (error) {
            toast.current?.show({ severity: "error", summary: "Export Failed", detail: error.message, life: 4000 });
        } finally {
            setIsExportingPDF(false);
        }
    };

    const header = (
        <div className="mb-4 p-3 border rounded bg-white">
            <div className="row g-3 align-items-center">
                <div className="col-md-3">
                    <div className="p-input-icon-left w-100">
                        <i className="pi pi-search" />
                        <InputText
                            placeholder="Search deposits..."
                            value={searchKeyword}
                            onChange={(e) => setSearchKeyword(e.target.value)}
                            className="w-100"
                            style={{ height: "40px", paddingLeft: "2.5rem" }}
                        />
                    </div>
                </div>
                <div className="col-md-2">
                    <Dropdown
                        value={selectedMonth}
                        options={MONTH_OPTIONS}
                        onChange={(e) => handleMonthChange(e.value)}
                        placeholder="Month"
                        className="w-100"
                    />
                </div>
                <div className="col-md-2">
                    <Dropdown
                        value={selectedYear}
                        options={yearOptions}
                        onChange={(e) => handleYearChange(e.value)}
                        placeholder="Year"
                        className="w-100"
                    />
                </div>
                <div className="col-md-1">
                    <Button
                        label="Clear"
                        className="p-button-text btn btn-secondary"
                        type="button"
                        onClick={() => {
                            setSearchKeyword("");
                            handleMonthChange(null);
                            handleYearChange(null);
                        }}
                    />
                </div>
                <div className="col-md-2">
                    <Button
                        label={isExportingExcel ? "Exporting..." : "Export Excel"}
                        icon="pi pi-file-excel"
                        className="btn btn-success w-100"
                        onClick={handleExportExcel}
                        disabled={isExportingExcel}
                        loading={isExportingExcel}
                    />
                </div>
                <div className="col-md-2">
                    <Button
                        label={isExportingPDF ? "Exporting..." : "Export PDF"}
                        icon="pi pi-file-pdf"
                        className="btn btn-danger w-100"
                        onClick={handleExportPDF}
                        disabled={isExportingPDF}
                        loading={isExportingPDF}
                    />
                </div>
            </div>
        </div>
    );

    const refundModalFooter = (
        <div>
            <Button label="Cancel" onClick={closeRefundModal} className="p-button-text btn btn-secondary me-2" disabled={isRefunding} />
            <Button
                label={isRefunding ? "Refunding..." : "Confirm Refund"}
                onClick={handleRefund}
                className="p-button btn btn-success"
                disabled={isRefunding || !refundAmount}
                loading={isRefunding}
            />
        </div>
    );

    const editModalFooter = (
        <div>
            <Button label="Cancel" onClick={closeEditModal} className="p-button-text btn btn-secondary me-2" disabled={isUpdating} />
            <Button
                label={isUpdating ? "Saving..." : "Save Changes"}
                onClick={handleUpdate}
                className="p-button btn btn-primary"
                disabled={isUpdating}
                loading={isUpdating}
            />
        </div>
    );

    const paymentModalFooter = (
        <div>
            <Button label="Cancel" onClick={closePaymentModal} className="p-button-text btn btn-secondary me-2" disabled={isPaying} />
            <Button
                label={isPaying ? "Recording..." : "Record Payment"}
                onClick={handleRecordPayment}
                className="p-button btn btn-primary"
                disabled={isPaying || !paymentAmount}
                loading={isPaying}
            />
        </div>
    );

    const receiptModalFooter = (
        <div>
            <Button label="Close" onClick={closeReceiptModal} className="p-button-text btn btn-secondary me-2" />
            <Button label="Download PDF" icon="pi pi-download" onClick={handleDownloadReceiptPDF} className="btn btn-primary" />
        </div>
    );

    const remainingForSelected = selectedDeposit
        ? (selectedDeposit.amount || 0) - (selectedDeposit.refundedAmount || 0)
        : 0;

    const dueForPaymentDeposit = paymentDeposit
        ? (paymentDeposit.amount || 0) - (paymentDeposit.amountPaid || 0)
        : 0;

    // What the customer would receive as contract credit if the entered
    // amount overshoots the remaining due — purely informational, the
    // server recomputes and applies this itself.
    const projectedOverpayment =
        paymentAmount && !isNaN(paymentAmount) && parseFloat(paymentAmount) > dueForPaymentDeposit
            ? parseFloat(paymentAmount) - dueForPaymentDeposit
            : 0;

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to="/facility/">Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">Levy Management</li>
                                <li className="breadcrumb-item">Levy Deposits</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <Toast ref={toast} />

            <Dialog
                header="Refund Deposit"
                visible={showRefundModal}
                style={{ width: "500px" }}
                onHide={closeRefundModal}
                footer={refundModalFooter}
                modal
                draggable={false}
            >
                {selectedDeposit && (
                    <div className="p-fluid">
                        <div className="alert alert-info mb-3" style={{ borderRadius: "8px", fontSize: "0.88rem" }}>
                            <div><strong>Customer:</strong> {customerTemplate(selectedDeposit)}</div>
                            <div><strong>Unit:</strong> {unitTemplate(selectedDeposit)}</div>
                            <div><strong>Deposit Amount:</strong> KES {fmt(selectedDeposit.amount)}</div>
                            <div><strong>Already Refunded:</strong> KES {fmt(selectedDeposit.refundedAmount)}</div>
                            <div><strong>Remaining Balance:</strong> KES {fmt(remainingForSelected)}</div>
                        </div>

                        <div className="field mb-3">
                            <label className="font-bold mb-2 d-block">Refund Amount <span className="text-danger">*</span></label>
                            <input
                                type="number"
                                className="form-control"
                                value={refundAmount}
                                onChange={(e) => setRefundAmount(e.target.value)}
                                max={remainingForSelected}
                                min="0.01"
                                step="0.01"
                                disabled={isRefunding}
                            />
                            <small className="text-muted">Maximum refundable: KES {fmt(remainingForSelected)}</small>
                        </div>

                        <div className="field mb-3">
                            <label className="font-bold mb-2 d-block">Refund Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={refundDate}
                                onChange={(e) => setRefundDate(e.target.value)}
                                disabled={isRefunding}
                            />
                        </div>

                        <div className="field mb-3">
                            <label className="font-bold mb-2 d-block">Refund Reference (Optional)</label>
                            <input
                                type="text"
                                className="form-control"
                                value={refundReference}
                                onChange={(e) => setRefundReference(e.target.value)}
                                placeholder="Transaction reference"
                                disabled={isRefunding}
                            />
                        </div>
                    </div>
                )}
            </Dialog>

            <Dialog
                header="Edit Deposit"
                visible={showEditModal}
                style={{ width: "500px" }}
                onHide={closeEditModal}
                footer={editModalFooter}
                modal
                draggable={false}
            >
                {editDeposit && (
                    <div className="p-fluid">
                        <div className="field mb-3">
                            <label className="font-bold mb-2 d-block">Date Paid</label>
                            <input
                                type="date"
                                className="form-control"
                                value={editDatePaid}
                                onChange={(e) => setEditDatePaid(e.target.value)}
                                disabled={isUpdating}
                            />
                        </div>

                        <div className="field mb-3">
                            <label className="font-bold mb-2 d-block">Receipt Number</label>
                            <input
                                type="text"
                                className="form-control"
                                value={editReceiptNumber}
                                onChange={(e) => setEditReceiptNumber(e.target.value)}
                                disabled={isUpdating}
                            />
                        </div>

                        <div className="field mb-3">
                            <label className="font-bold mb-2 d-block">Payment Reference</label>
                            <input
                                type="text"
                                className="form-control"
                                value={editPaymentReference}
                                onChange={(e) => setEditPaymentReference(e.target.value)}
                                placeholder="Transaction reference"
                                disabled={isUpdating}
                            />
                        </div>

                        <small className="text-muted">
                            Amount and status can't be edited here — refunds are processed through the Refund action.
                        </small>
                    </div>
                )}
            </Dialog>

            <Dialog
                header="Record Deposit Payment"
                visible={showPaymentModal}
                style={{ width: "500px" }}
                onHide={closePaymentModal}
                footer={paymentModalFooter}
                modal
                draggable={false}
            >
                {paymentDeposit && (
                    <div className="p-fluid">
                        <div className="alert alert-info mb-3" style={{ borderRadius: "8px", fontSize: "0.88rem" }}>
                            <div><strong>Customer:</strong> {customerTemplate(paymentDeposit)}</div>
                            <div><strong>Unit:</strong> {unitTemplate(paymentDeposit)}</div>
                            <div><strong>Deposit Amount:</strong> KES {fmt(paymentDeposit.amount)}</div>
                            <div><strong>Already Paid:</strong> KES {fmt(paymentDeposit.amountPaid)}</div>
                            <div><strong>Amount Due:</strong> KES {fmt(dueForPaymentDeposit)}</div>
                        </div>

                        <div className="field mb-3">
                            <label className="font-bold mb-2 d-block">Payment Amount <span className="text-danger">*</span></label>
                            <input
                                type="number"
                                className="form-control"
                                value={paymentAmount}
                                onChange={(e) => setPaymentAmount(e.target.value)}
                                min="0.01"
                                step="0.01"
                                disabled={isPaying}
                            />
                            <small className="text-muted">
                                Partial payments are allowed. Paying more than the amount due (KES {fmt(dueForPaymentDeposit)})
                                credits the excess to the contract instead of the deposit.
                            </small>
                            {projectedOverpayment > 0 && (
                                <div className="alert alert-warning mt-2 mb-0" style={{ borderRadius: "8px", fontSize: "0.85rem" }}>
                                    KES {fmt(projectedOverpayment)} will be applied as credit to the contract.
                                </div>
                            )}
                        </div>

                        <div className="field mb-3">
                            <label className="font-bold mb-2 d-block">Payment Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={paymentDate}
                                onChange={(e) => setPaymentDate(e.target.value)}
                                disabled={isPaying}
                            />
                        </div>

                        <div className="field mb-3">
                            <label className="font-bold mb-2 d-block">Payment Reference (Optional)</label>
                            <input
                                type="text"
                                className="form-control"
                                value={paymentReference}
                                onChange={(e) => setPaymentReference(e.target.value)}
                                placeholder="Transaction reference"
                                disabled={isPaying}
                            />
                        </div>

                        <div className="field mb-3">
                            <label className="font-bold mb-2 d-block">Payment Method (Optional)</label>
                            <input
                                type="text"
                                className="form-control"
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                                placeholder="e.g. M-Pesa, Bank Transfer, Cash"
                                disabled={isPaying}
                            />
                        </div>
                    </div>
                )}
            </Dialog>

            <Dialog
                header="Deposit Receipt"
                visible={showReceiptModal}
                style={{ width: "650px" }}
                onHide={closeReceiptModal}
                footer={receiptModalFooter}
                modal
                draggable={false}
            >
                {receiptDeposit && (
                    <div ref={receiptRef} style={receiptStyles.receiptContainer}>
                        <div style={receiptStyles.header}>
                            <div style={receiptStyles.headerTitle}>RECEIPT</div>
                            <div style={receiptStyles.headerSubtitle}>
                                {receiptDeposit.facilityId?.name || "PayServe"}
                            </div>
                        </div>

                        <div style={receiptStyles.content}>
                            <div style={receiptStyles.section}>
                                <div style={receiptStyles.detailsGrid}>
                                    <span style={receiptStyles.label}>Receipt No:</span>
                                    <span style={receiptStyles.value}>{receiptDeposit.receiptNumber || "—"}</span>
                                </div>
                                <div style={receiptStyles.detailsGrid}>
                                    <span style={receiptStyles.label}>Date Paid:</span>
                                    <span style={receiptStyles.value}>{fmtDate(receiptDeposit.datePaid)}</span>
                                </div>
                                {receiptDeposit.paymentReference && (
                                    <div style={receiptStyles.detailsGrid}>
                                        <span style={receiptStyles.label}>Payment Ref:</span>
                                        <span style={receiptStyles.value}>{receiptDeposit.paymentReference}</span>
                                    </div>
                                )}
                            </div>

                            <div style={receiptStyles.section}>
                                <div style={receiptStyles.sectionTitle}>Received From</div>
                                <div style={{ textAlign: "center", fontSize: "1.35rem", marginBottom: "0.5rem" }}>
                                    {customerTemplate(receiptDeposit)}
                                </div>
                                <div style={{ textAlign: "center", fontSize: "0.95rem", color: "#4b5563" }}>
                                    Unit: {unitTemplate(receiptDeposit)}
                                </div>
                            </div>

                            <div style={receiptStyles.section}>
                                <div style={receiptStyles.detailsGrid}>
                                    <span style={receiptStyles.label}>Deposit Amount:</span>
                                    <span style={receiptStyles.value}>KES {fmt(receiptDeposit.amount)}</span>
                                </div>
                                <div style={receiptStyles.detailsGrid}>
                                    <span style={receiptStyles.label}>Refunded Amount:</span>
                                    <span style={receiptStyles.value}>KES {fmt(receiptDeposit.refundedAmount)}</span>
                                </div>
                                <div style={receiptStyles.detailsGrid}>
                                    <span style={{ ...receiptStyles.label, fontWeight: "600" }}>Balance Held:</span>
                                    <span style={{ ...receiptStyles.value, fontWeight: "600" }}>{balanceTemplate(receiptDeposit)}</span>
                                </div>
                            </div>
                        </div>

                        <div style={receiptStyles.footer}>
                            <p style={{ margin: 0 }}>Thank you for your payment!</p>
                            <p style={{ margin: 0 }}>This is a computer generated receipt.</p>
                        </div>
                    </div>
                )}
            </Dialog>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-header">
                            <h5>Levy Deposits</h5>
                        </div>
                        <div className="card-body">

                            <div className="row mb-4">
                                <div className="col-md-12">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="row">
                                                {[
                                                    ["ti-cash-banknote", "text-info", `KES ${fmt(totalDepositAmount)}`, "Total Deposits", "h6"],
                                                    ["ti-clock", "text-secondary", `KES ${fmt(totalDue)}`, "Awaiting Payment", "h6"],
                                                    ["ti-lock", "text-warning", `KES ${fmt(totalHeld)}`, "Currently Held", "h6"],
                                                    ["ti-cash-banknote", "text-success", `KES ${fmt(totalRefunded)}`, "Total Refunded", "h6"],
                                                    ["ti-receipt", "text-primary", totalDepositsCount, "Total Deposits Count", "h5"],
                                                ].map(([icon, color, val, label, tag], i) => (
                                                    <div key={i} className="col-md-3">
                                                        <div className="text-center p-3 border rounded bg-light">
                                                            <i className={`ti ${icon} ${color} f-24 mb-2`}></i>
                                                            {React.createElement(tag, { className: `mb-0 ${color}` }, val)}
                                                            <small className="text-muted">{label}</small>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                                <ul className="nav nav-tabs invoice-tab border-bottom" id="depositTab" role="tablist">
                                    {[
                                        ["All", allDeposits, "bg-light-primary", 0],
                                        ["Pending", pendingDeposits, "bg-light-secondary", 1],
                                        ["Held", heldDeposits, "bg-light-warning", 2],
                                        ["Refunded", refundedDeposits, "bg-light-success", 3],
                                        ["Forfeited", forfeitedDeposits, "bg-light-danger", 4],
                                    ].map(([label, data, cls, idx]) => (
                                        <li key={idx} className="nav-item" role="presentation">
                                            <button
                                                className={`nav-link ${activeTabIndex === idx ? "active" : ""}`}
                                                onClick={() => setActiveTabIndex(idx)}
                                                type="button"
                                            >
                                                <span className="d-flex align-items-center gap-2">
                                                    {label} <span className={`avtar rounded-circle ${cls}`}>{data.length}</span>
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <DataTable
                                value={getDepositsByTab(activeTabIndex)}
                                header={header}
                                loading={loading}
                                paginator
                                rows={rowsPerPage}
                                rowsPerPageOptions={[10, 20, 50, 100]}
                                totalRecords={totalRecords}
                                lazy
                                first={(page - 1) * rowsPerPage}
                                onPage={(e) => { setPage(e.page + 1); setRowsPerPage(e.rows); }}
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} deposits — Page {currentPage} of {totalPages}"
                                emptyMessage="No deposits found"
                                className="p-datatable-sm"
                                stripedRows
                                sortField="createdAt"
                                sortOrder={-1}
                                responsiveLayout="scroll"
                            >
                                <Column field="receiptNumber" header="Receipt #" style={{ minWidth: "150px" }} />
                                <Column header="Customer" body={customerTemplate} style={{ minWidth: "180px" }} />
                                <Column header="Unit" body={unitTemplate} style={{ minWidth: "120px" }} />
                                <Column field="amount" header="Amount" body={(r) => `KES ${fmt(r.amount)}`} style={{ minWidth: "130px" }} />
                                <Column field="amountPaid" header="Paid" body={(r) => `KES ${fmt(r.amountPaid)}`} style={{ minWidth: "130px" }} />
                                <Column field="refundedAmount" header="Refunded" body={(r) => `KES ${fmt(r.refundedAmount)}`} style={{ minWidth: "130px" }} />
                                <Column header="Balance" body={balanceTemplate} style={{ minWidth: "150px" }} />
                                <Column field="datePaid" header="Date Paid" body={(r) => fmtDate(r.datePaid)} style={{ minWidth: "120px" }} />
                                <Column field="status" header="Status" body={statusTemplate} style={{ minWidth: "140px" }} />
                                <Column header="Actions" body={actionTemplate} style={{ minWidth: "200px" }} />
                            </DataTable>

                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default FacilityLevyDeposits;